import React, { createContext, useReducer, useContext, useEffect, useMemo } from 'react';
import type { User } from '../types/user';
import {
  getAuthToken,
  getAuthUser,
  setAuthToken,
  setAuthUser,
  clearAuthCookies,
} from '../utils/cookies';
import normalizeUser from '../utils/normalizeUser';
import { setAuthExpiredHandler } from '../utils/authExpiredHandler';

export interface UserState {
  user: User | null;
  token: string | null;
  isInitialized: boolean;
}

type UserAction =
  | { type: 'SET_AUTH'; payload: { token: string; user: User } }
  | { type: 'LOGOUT' }
  | { type: 'RESTORE_SESSION'; payload: { token: string; user: User } | null };

const initialState: UserState = {
  user: null,
  token: null,
  isInitialized: false,
};

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_AUTH':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isInitialized: true,
      };
    case 'RESTORE_SESSION':
      return {
        ...state,
        user: action.payload?.user ?? null,
        token: action.payload?.token ?? null,
        isInitialized: true,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isInitialized: true,
      };
    default:
      return state;
  }
}

interface UserContextValue extends UserState {
  isAuthenticated: boolean;
  setAuth: (token: string, user: User) => void;
  /** Replace token and mark user as having uploaded INE (e.g. after upload-ine API). */
  setTokenAndIneUploaded: (token: string) => void;
  logout: () => void;
  /** Use for authenticated API calls: Authorization: Bearer <token> */
  getAuthHeader: () => { Authorization: string } | null;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [state, dispatch] = useReducer(userReducer, initialState);

  useEffect(() => {
    const token = getAuthToken();
    const user = getAuthUser();
    if (token && user) {
      const normalized = normalizeUser(user);
      setAuthUser(normalized);
      dispatch({ type: 'RESTORE_SESSION', payload: { token, user: normalized } });
    } else {
      dispatch({ type: 'RESTORE_SESSION', payload: null });
    }
  }, []);

  const setAuth = React.useCallback((token: string, user: User) => {
    const normalized = normalizeUser(user);
    setAuthToken(token);
    setAuthUser(normalized);
    dispatch({ type: 'SET_AUTH', payload: { token, user: normalized } });
  }, []);

  const setTokenAndIneUploaded = React.useCallback(
    (token: string) => {
      setAuthToken(token);
      const base = state.user ?? getAuthUser();
      if (!base) {
        return;
      }
      const updatedUser = normalizeUser({ ...base, ineUploaded: true });
      setAuthUser(updatedUser);
      dispatch({ type: 'SET_AUTH', payload: { token, user: updatedUser } });
    },
    [state.user]
  );

  const logout = React.useCallback(() => {
    clearAuthCookies();
    dispatch({ type: 'LOGOUT' });
  }, []);

  useEffect(() => {
    setAuthExpiredHandler(logout);
    return () => setAuthExpiredHandler(null);
  }, [logout]);

  const getAuthHeader = React.useCallback((): { Authorization: string } | null => {
    const t = state.token ?? getAuthToken();
    return t ? { Authorization: `Bearer ${t}` } : null;
  }, [state.token]);

  const value = useMemo<UserContextValue>(
    () => ({
      ...state,
      isAuthenticated: Boolean(state.token && state.user),
      setAuth,
      setTokenAndIneUploaded,
      logout,
      getAuthHeader,
    }),
    [state, setAuth, setTokenAndIneUploaded, logout, getAuthHeader]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (ctx == null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return ctx;
}
